<template>
  <div>
    <v-carousel
      v-if="images && images.length > 0"
      :height="height"
      hide-delimiter-background
      show-arrows="hover"
      :cycle="false"
    >
      <v-carousel-item
        v-for="(image, idx) in images"
        :key="image.id || idx"
      >
        <v-img
          :src="resolveStorageUrl(image.thumbnailUrl)"
          height="100%"
          class="clickable carousel-image"
          @click="enlarge(image)"
        />
      </v-carousel-item>
    </v-carousel>
    <div
      v-else
      class="empty-carousel"
      :style="{ height: typeof height === 'number' ? height + 'px' : height }"
    >
      <v-icon size="64" color="grey">mdi-image-off-outline</v-icon>
    </div>
    <v-dialog v-model="enlargeDialog" max-width="900px">
      <v-card>
        <v-card-text class="pa-2 d-flex justify-center align-center">
          <v-img :src="enlargedUrl" max-height="80vh" />
        </v-card-text>
        <v-expansion-panels
          v-if="metadataRows.length"
          flat
          class="px-2 pb-2"
        >
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon size="small" class="mr-2">mdi-information-outline</v-icon>
              Photo details
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-table density="compact">
                <tbody>
                  <tr
                    v-for="row in metadataRows"
                    :key="row.label"
                  >
                    <td class="text-medium-emphasis metadata-label">{{ row.label }}</td>
                    <td>{{ row.value }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="enlargeDialog = false">
            <v-icon>mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    height: {
      type: [Number, String],
      default: 280,
    },
  },
  data() {
    return {
      enlargeDialog: false,
      enlargedUrl: "",
      enlargedImage: null,
    };
  },
  computed: {
    metadataRows() {
      const image = this.enlargedImage;
      if (!image) return [];
      const rows = [];
      const push = (label, value) => {
        if (value !== null && value !== undefined && value !== "") {
          rows.push({ label, value });
        }
      };

      if (image.takenAt) push("Taken", this.formatDateTime(image.takenAt));
      if (image.latitude !== null && image.latitude !== undefined
          && image.longitude !== null && image.longitude !== undefined) {
        push("Location", `${this.formatCoord(image.latitude)}, ${this.formatCoord(image.longitude)}`);
      }
      if (image.gpsDirection !== null && image.gpsDirection !== undefined) {
        push("Bearing", `${Math.round(image.gpsDirection)}\u00b0`);
      }
      if (image.gpsAltitude !== null && image.gpsAltitude !== undefined) {
        push("Altitude", `${Math.round(image.gpsAltitude)} m`);
      }
      push("Camera", [image.cameraMake, image.cameraModel].filter(Boolean).join(" "));
      push("Lens", image.lensModel);
      const exposure = [
        image.focalLength !== null && image.focalLength !== undefined ? `${image.focalLength}mm` : null,
        image.fNumber !== null && image.fNumber !== undefined ? `f/${image.fNumber}` : null,
        image.exposureTime || null,
        image.iso !== null && image.iso !== undefined ? `ISO ${image.iso}` : null,
      ].filter(Boolean).join(" \u00b7 ");
      push("Exposure", exposure);
      if (image.pixelWidth && image.pixelHeight) {
        push("Dimensions", `${image.pixelWidth} \u00d7 ${image.pixelHeight}`);
      }
      push("Photographer", image.artist);
      push("Copyright", image.copyrightNotice);
      push("Software", image.software);
      return rows;
    },
  },
  methods: {
    resolveStorageUrl(url) {
      if (!url) return "";
      if (url.startsWith("http")) return url;
      return `${process.env.VUE_APP_API_URL}${url}`;
    },
    enlarge(image) {
      this.enlargedImage = image;
      this.enlargedUrl = this.resolveStorageUrl(image.imageUrl || image.thumbnailUrl);
      this.enlargeDialog = true;
    },
    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    },
    formatCoord(value) {
      const num = Number(value);
      return Number.isNaN(num) ? String(value) : num.toFixed(5);
    },
  },
};
</script>

<style scoped>
.carousel-image {
  background-color: #f5f5f5;
}
.clickable {
  cursor: pointer;
}
.metadata-label {
  width: 40%;
}
.empty-carousel {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
